<template>
  <div>
    <!-- the field can be left out (hide-input): the parent opens the dialog itself through open() -->
    <v-text-field
      v-if="!hideInput"
      class="px-2"
      :value="selectedText"
      :label="label"
      hide-details
      single-line
      readonly
      prepend-inner-icon="mdi-calendar-range"
      append-icon="mdi-calendar"
      style="min-width: 300px;"
      @click="open"
      data-cy="production-days"
    />
    <v-dialog v-model="showDialog" width="auto">
      <v-card v-if="!showDatePicker">
        <v-card-title>
          <v-spacer/>
          Select production days
          <v-spacer/>
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-for="week of upcomingWeeks" v-bind:key="week.key" class="mb-3">
            <v-btn
              width="100%"
              outlined
              large
              :data-dates="week.key"
              data-cy="production-days-option"
              @click="choose(week.dates)"
            >
              {{ week.text }}
            </v-btn>
          </div>
          <div v-if="upcomingWeeks.length === 0" class="grey--text text-center">no upcoming productions</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined x-small @click="openDatePicker">Other dates</v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-text>
          <v-date-picker v-model="pickerDate" no-title :allowed-dates="allowedDates" @input="chooseDay"/>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined x-small @click="showDatePicker = false">Upcoming productions</v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/**
 * Picks a set of production days (Mon-Wed or Thu-Sat) without a production: the
 * print macros replay for these dates and each page keeps the production or
 * shipping group it was recorded with. Lists the upcoming productions' date sets
 * like ProductionSelector does, plus a calendar for any other day, expanded to
 * its production days.
 */
import moment from 'moment/moment';
import {mapActions, mapState} from 'vuex';
import {getProductionDays} from '@/store/utils';

const DAY = 'ddd';
const DATE = 'MMM D';

function describeDays(dates) {
  const sorted = [...dates].sort();
  const from = moment(sorted[0]);
  const to = moment(sorted[sorted.length - 1]);
  return `${from.format(DAY)}-${to.format(DAY)} (${from.format(DATE)}-${to.format(DATE)})`;
}

export default {
  name: 'ProductionDaysSelector',
  props: {
    dates: {type: Array, default: () => []},
    label: {type: String, default: 'Production days'},
    hideInput: {type: Boolean, default: false},
  },
  data() {
    return {
      showDialog: false,
      showDatePicker: false,
      pickerDate: null,
    };
  },
  async mounted() {
    await this.fetchUpcomingProductions();
  },
  computed: {
    ...mapState(['productions']),
    /** each distinct set of dates among the upcoming productions, earliest first */
    upcomingWeeks() {
      const weeks = new Map();
      (this.productions || []).forEach((p) => {
        const dates = [...(p.dates || [])].filter(Boolean).sort();
        if (dates.length > 0 && !weeks.has(dates.join(','))) {
          weeks.set(dates.join(','), {key: dates.join(','), dates, text: describeDays(dates)});
        }
      });
      return [...weeks.values()].sort((a, b) => a.key.localeCompare(b.key));
    },
    selectedText() {
      return this.dates && this.dates.length > 0 ? describeDays(this.dates) : '';
    },
  },
  watch: {
    /** the picker went away without a choice (or after one: the update came first) */
    showDialog(open) {
      if (!open) {
        this.$emit('close');
      }
    },
  },
  methods: {
    ...mapActions(['fetchUpcomingProductions']),
    /** show the picker; for pages that need the days chosen before they can do anything */
    open() {
      this.showDialog = true;
    },
    openDatePicker() {
      this.pickerDate = (this.dates && this.dates[0]) || null;
      this.showDatePicker = true;
    },
    allowedDates(date) {
      return moment(date).day() !== 0;
    },
    choose(dates) {
      this.showDialog = false;
      this.showDatePicker = false;
      // hand the parent its own copy so it never shares an array with the store
      const copy = [...dates].sort();
      this.$emit('update:dates', copy);
      this.$emit('update', {dates: copy});
    },
    chooseDay(date) {
      if (date) {
        this.choose(getProductionDays(date));
      }
    },
  },
};
</script>
